<template>
  <v-card tile>
    <v-card-text>
      <v-row class="mt-16">
        <v-col cols="12">
          <v-chip label color="transparent" text-color="black">
            <h2>Gestión de Productos</h2>
          </v-chip>
        </v-col>
      </v-row>

      <!-- Filtros -->
      <v-row class="mt-2">
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="filtros.nombre"
            prepend-icon="mdi-magnify"
            label="Nombre"
            dense
            clearable
            @keypress.enter="buscarProductos"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="filtros.codigo"
            prepend-icon="mdi-barcode"
            label="Código"
            dense
            clearable
            @keypress.enter="buscarProductos"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-select
            v-model="filtros.estado"
            :items="estados"
            label="Estado"
            dense
            clearable
            prepend-icon="mdi-check-circle"
          ></v-select>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-btn color="primary" @click="buscarProductos" :loading="loading">
            Buscar
            <v-icon right>mdi-magnify</v-icon>
          </v-btn>
          <v-btn color="grey" class="ml-2" @click="limpiarFiltros">
            Limpiar
          </v-btn>
          <v-btn color="success" class="ml-2" @click="abrirNuevoProducto">
            <v-icon left>mdi-plus</v-icon>
            Nuevo Producto
          </v-btn>
          <v-btn color="info" class="ml-2" @click="irAlertas">
            <v-icon left>mdi-alert</v-icon>
            Alertas de Stock
          </v-btn>
          <v-btn
            color="purple"
            dark
            class="ml-2"
            @click="exportarPDFProductos"
            :disabled="!productos.length"
          >
            <v-icon left>mdi-file-pdf-box</v-icon>
            PDF Productos
          </v-btn>
          <v-btn
            color="purple darken-2"
            dark
            class="ml-2"
            @click="exportarPDFAlertas"
          >
            <v-icon left>mdi-file-pdf-box</v-icon>
            PDF Alertas Stock
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Tabla de productos -->
    <v-card-text v-if="productos.length > 0">
      <v-data-table
        :headers="headers"
        :items="productos"
        class="elevation-1"
        locale="es-ar"
        item-key="idProducto"
        :items-per-page="10"
        :footer-props="{
          itemsPerPageText: 'Filas por página',
          itemsPerPageOptions: [5, 10, 15, 20],
        }"
        :loading="loading"
      >
        <template v-slot:item.stockActual="{ item }">
          <v-chip :color="getStockColor(item)" dark small>
            {{ item.stockActual }}
          </v-chip>
        </template>

        <template v-slot:item.precioVenta="{ item }">
          ${{ item.precioVenta.toFixed(2) }}
        </template>

        <template v-slot:item.estado="{ item }">
          <v-chip
            :color="item.estado === 'activo' ? 'success' : 'grey'"
            dark
            small
            style="text-transform: capitalize"
          >
            {{ item.estado }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                medium
                color="primary"
                class="mr-3"
                v-on="on"
                v-bind="attrs"
                @click="verMovimientos(item)"
              >
                mdi-history
              </v-icon>
            </template>
            <span>Ver movimientos</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                medium
                color="purple"
                class="mr-3"
                v-on="on"
                v-bind="attrs"
                @click="editarProducto(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <span>Editar producto</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                medium
                color="red"
                v-on="on"
                v-bind="attrs"
                @click="eliminarProducto(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <span>Eliminar producto</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card-text>

    <v-card-text v-else-if="!loading && busquedaRealizada">
      <v-alert color="purple" dark outlined>
        No se encontraron productos con los filtros aplicados.
      </v-alert>
    </v-card-text>

    <!-- Modales -->
    <NuevoProducto
      :visible="showNuevoProducto"
      @close="cierraNuevoProducto"
      @guardado="handleGuardado"
    ></NuevoProducto>

    <EditarProducto
      :visible="showEditarProducto"
      :idProducto="productoSeleccionado"
      @close="cierraEditarProducto"
      @actualizado="handleActualizado"
    ></EditarProducto>

    <!-- Modal movimientos -->
    <MovimientosStock
      :visible="showMovimientos"
      :idProducto="productoMovimientos ? productoMovimientos.idProducto : 0"
      :nombreProducto="productoMovimientos ? productoMovimientos.nombre : ''"
      :stockInicial="
        productoMovimientos ? productoMovimientos.stockInicial : null
      "
      :stockMinimo="
        productoMovimientos ? productoMovimientos.stockMinimo : null
      "
      :puntoReposicion="
        productoMovimientos ? productoMovimientos.puntoReposicion : null
      "
      @close="showMovimientos = false"
      @stockActualizado="buscarProductos"
    ></MovimientosStock>

    <!-- Modal alertas de stock -->
    <v-dialog v-model="showAlertas" max-width="800px">
      <v-card class="mb-0">
        <v-toolbar dark color="warning">
          <v-btn icon dark @click="showAlertas = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
          <v-toolbar-title>Alertas de Stock</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-data-table
            v-if="productosAlerta.length"
            :headers="headersAlerta"
            :items="productosAlerta"
            class="elevation-1"
            locale="es-ar"
            item-key="idProducto"
            :items-per-page="20"
            dense
            :hide-default-footer="productosAlerta.length <= 20"
          >
            <template v-slot:item.alerta="{ item }">
              <v-chip
                x-small
                :color="
                  item.stockActual <= 0
                    ? 'error'
                    : item.stockActual <= item.stockMinimo
                    ? 'error'
                    : 'warning'
                "
                dark
              >
                {{
                  item.stockActual <= 0
                    ? "Sin stock"
                    : item.stockActual <= item.stockMinimo
                    ? "Bajo mínimo"
                    : "Reponer"
                }}
              </v-chip>
            </template>
            <template v-slot:item.stockActual="{ item }">
              <strong
                :class="
                  item.stockActual <= item.stockMinimo
                    ? 'red--text'
                    : 'orange--text'
                "
              >
                {{ item.stockActual }}
              </strong>
            </template>
          </v-data-table>
          <v-alert v-else color="success" outlined dense>
            No hay productos con alertas de stock activas.
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from "@/axios-auth";
import NuevoProducto from "./NuevoProducto.vue";
import EditarProducto from "./EditarProducto.vue";
import MovimientosStock from "./MovimientosStock.vue";
import { pdfListadoProductos, pdfAlertasStock } from "./reportesStockPDF";

export default {
  components: {
    NuevoProducto,
    EditarProducto,
    MovimientosStock,
  },
  data() {
    return {
      filtros: {
        nombre: "",
        codigo: "",
        estado: null,
      },
      estados: [
        { text: "Todos", value: "0" },
        { text: "Activo", value: "activo" },
        { text: "Inactivo", value: "inactivo" },
      ],
      productos: [],
      headers: [
        { text: "Código", value: "codigo" },
        { text: "Nombre", value: "nombre" },
        { text: "Stock Actual", value: "stockActual" },
        { text: "Stock Mínimo", value: "stockMinimo" },
        { text: "Precio Venta", value: "precioVenta" },
        { text: "Estado", value: "estado" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      loading: false,
      busquedaRealizada: false,
      showNuevoProducto: false,
      showEditarProducto: false,
      productoSeleccionado: 0,
      showMovimientos: false,
      productoMovimientos: null,
      showAlertas: false,
      productosAlerta: [],
      headersAlerta: [
        { text: "Código", value: "codigo" },
        { text: "Nombre", value: "nombre" },
        { text: "Stock Actual", value: "stockActual" },
        { text: "Stock Mínimo", value: "stockMinimo" },
        { text: "Punto Reposición", value: "puntoReposicion" },
        { text: "Alerta", value: "alerta", sortable: false },
      ],
    };
  },
  methods: {
    buscarProductos() {
      this.loading = true;
      this.busquedaRealizada = true;

      const params = {
        nombre: this.filtros.nombre,
        codigo: this.filtros.codigo,
        estado: this.filtros.estado,
        idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
      };

      let self = this;
      axios
        .get("/Producto/GetProductos", { params })
        .then(function (response) {
          if (response.data && response.data.length > 0) {
            self.productos = response.data;
          } else {
            self.productos = [];
          }
          self.loading = false;
        })
        .catch(function (error) {
          self.loading = false;
          self.showAlert({
            icon: "error",
            title: "Error al buscar productos.",
            vm: self,
            timer: 2000,
          });
        });
    },

    getStockColor(item) {
      if (item.stockActual <= 0) return "error";
      if (item.stockActual <= item.stockMinimo) return "error";
      if (item.stockActual <= item.puntoReposicion) return "warning";
      return "success";
    },

    limpiarFiltros() {
      this.filtros.nombre = "";
      this.filtros.codigo = "";
      this.filtros.estado = null;
      this.productos = [];
      this.busquedaRealizada = false;
    },

    abrirNuevoProducto() {
      this.showNuevoProducto = true;
    },

    cierraNuevoProducto() {
      this.showNuevoProducto = false;
    },

    handleGuardado() {
      this.showNuevoProducto = false;
      this.buscarProductos();
    },

    editarProducto(item) {
      this.productoSeleccionado = item.idProducto;
      this.showEditarProducto = true;
    },

    cierraEditarProducto() {
      this.showEditarProducto = false;
    },

    handleActualizado() {
      this.showEditarProducto = false;
      this.buscarProductos();
    },

    verMovimientos(item) {
      this.productoMovimientos = item;
      this.showMovimientos = true;
    },

    eliminarProducto(item) {
      this.$swal({
        title: "Eliminar Producto",
        text: "¿Desea eliminar este producto? Esta acción no se puede deshacer.",
        icon: "warning",
        type: "warning",
        showCancelButton: true,
        background: "#ececec",
        confirmButtonText: "Si, eliminar",
        cancelButtonText: "Cancelar",
        showCloseButton: true,
      }).then((result) => {
        if (result.value) {
          let self = this;
          axios
            .get("/Producto/BorrarProducto", {
              params: {
                idProducto: item.idProducto,
              },
            })
            .then((response) => {
              self.showAlert({
                icon: "success",
                title: "Producto eliminado exitosamente.",
                vm: self,
                timer: 2000,
              });
              self.buscarProductos();
            })
            .catch((error) => {
              self.showAlert({
                icon: "warning",
                title:
                  error.response?.data || "No se pudo eliminar el producto.",
                vm: self,
                timer: 3000,
              });
            });
        }
      });
    },

    irAlertas() {
      // Filtrar productos con stock bajo mínimo o bajo punto de reposición
      this.productosAlerta = this.productos
        .filter((p) => p.stockActual <= p.puntoReposicion)
        .sort((a, b) => a.stockActual - b.stockActual);

      if (!this.busquedaRealizada || !this.productos.length) {
        // Si no hay búsqueda previa, cargar todos los productos primero
        const params = {
          nombre: "%",
          codigo: "%",
          estado: "activo",
          idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
        };
        axios.get("/Producto/GetProductos", { params }).then((r) => {
          this.productosAlerta = (r.data || [])
            .filter((p) => p.stockActual <= p.puntoReposicion)
            .sort((a, b) => a.stockActual - b.stockActual);
          this.showAlertas = true;
        });
        return;
      }
      this.showAlertas = true;
    },

    exportarPDFProductos() {
      const nombreInstitucion =
        this.$store.state.usuario.institucion.nombreInstitucion
          .toString()
          .trim();
      pdfListadoProductos(this.productos, nombreInstitucion, this.filtros);
    },

    exportarPDFAlertas() {
      const nombreInstitucion =
        this.$store.state.usuario.institucion.nombreInstitucion
          .toString()
          .trim();
      const params = {
        nombre: "%",
        codigo: "%",
        estado: "activo",
        idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
      };
      axios.get("/Producto/GetProductos", { params }).then((r) => {
        const alertas = (r.data || [])
          .filter((p) => p.stockActual <= p.puntoReposicion)
          .sort((a, b) => a.stockActual - b.stockActual);
        if (alertas.length === 0) {
          this.showAlert({
            icon: "info",
            title: "No hay productos con alertas de stock.",
            vm: this,
            timer: 2500,
          });
          return;
        }
        pdfAlertasStock(alertas, nombreInstitucion);
      });
    },

    showAlert(alertInfo) {
      alertInfo.vm.$swal.fire({
        icon: alertInfo.icon,
        title: alertInfo.title,
        showConfirmButton: false,
        background: "#ececec",
        timer: alertInfo.timer,
      });
    },
  },
  created() {
    this.buscarProductos();
    this.filtros.estado = "0";
  },
};
</script>
